// frontend/src/components/compliance/modals/OverdueTasksModal.jsx

import React, { useMemo } from "react";
import {
  safeNumber,
  normalizeText,
  cleanValue,
  buildControlLabel,
  getTaskStatusLabel,
} from "../../../utils/complianceFormatters";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

function timestampMs(value) {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function compactDate(value) {
  if (!value) return "No deadline";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getDeadlineValue(task = {}) {
  return task.due_date || task.dueDate || task.deadline_at || task.deadlineAt || null;
}

function getOverdueDays(task = {}) {
  const directDays = safeNumber(task.days_until_due, null);

  if (Number.isFinite(directDays) && directDays < 0) {
    return Math.abs(directDays);
  }

  const deadline = getDeadlineValue(task);
  const parsed = timestampMs(deadline);
  if (!parsed) return null;

  const diffMs = Date.now() - parsed;
  if (diffMs <= 0) return 0;

  return Math.max(0, Math.ceil(diffMs / 86400000));
}

function getOverdueLabel(task = {}) {
  const days = getOverdueDays(task);

  if (days === null) return "Overdue";
  if (days === 0) return "Due today / breached";
  return `Overdue by ${days} day${days === 1 ? "" : "s"}`;
}

function getOldestBreachDays(tasks = []) {
  const days = tasks
    .map((task) => getOverdueDays(task))
    .filter((value) => Number.isFinite(value));

  if (!days.length) return 0;
  return Math.max(...days);
}

function getSlaSource(task = {}) {
  const source =
    task.deadline_source ||
    task.deadlineSource ||
    task.sla_source ||
    task.slaSource ||
    task.sla_state ||
    "";
  const cleaned = cleanValue(source, "SLA deadline");
  return cleaned.replace(/_/g, " ");
}

function getTaskResource(task = {}) {
  return cleanValue(task.resource_id || task.resourceId || task.task_metadata?.resource_id, "—");
}

function getTaskTitle(task = {}) {
  const control = task.control_id || task.controlId || "";
  const rule = task.rule_id || task.ruleId || "";
  return (
    task.title ||
    task.task_title ||
    task.name ||
    `Remediate ${buildControlLabel(control, rule)}`
  );
}

function getValidationLabel(task = {}) {
  const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");
  if (validationStatus === "failed") return "Failed";
  if (validationStatus === "passed") return "Passed";
  if (validationStatus === "approved_exception") return "Approved Exception";
  if (validationStatus === "manual_override") return "Manual Override";
  if (validationStatus === "pending") return "Pending";
  return validationStatus ? validationStatus.replace(/_/g, " ") : "Pending";
}

function isTaskHighPriority(task = {}) {
  return safeNumber(task.priority, 0) >= 100;
}

function isTaskUnassigned(task = {}) {
  return !cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "");
}

function sortMostOverdueFirst(tasks = []) {
  return [...(Array.isArray(tasks) ? tasks : [])].sort((left, right) => {
    const rightDays = getOverdueDays(right);
    const leftDays = getOverdueDays(left);

    if (Number.isFinite(rightDays) && Number.isFinite(leftDays) && rightDays !== leftDays) {
      return rightDays - leftDays;
    }

    const leftDeadline = timestampMs(getDeadlineValue(left));
    const rightDeadline = timestampMs(getDeadlineValue(right));
    if (leftDeadline && rightDeadline && leftDeadline !== rightDeadline) {
      return leftDeadline - rightDeadline;
    }

    return timestampMs(right.updated_at || right.created_at) - timestampMs(left.updated_at || left.created_at);
  });
}

export default function OverdueTasksModal({
  open,
  framework,
  overdueTasks = [],
  totalCount = 0,
  loading = false,
  error = "",
  onClose,
  onReviewTask,
  onFixPlan,
  onViewGuidance,
  onOpenDrift,
}) {
  const sortedTasks = useMemo(() => sortMostOverdueFirst(overdueTasks), [overdueTasks]);

  if (!open) return null;

  const shownCount = sortedTasks.length;
  const effectiveTotalCount = Math.max(shownCount, safeNumber(totalCount, shownCount));
  const highPriorityCount = sortedTasks.filter(isTaskHighPriority).length;
  const unassignedCount = sortedTasks.filter(isTaskUnassigned).length;
  const oldestBreachDays = getOldestBreachDays(sortedTasks);

  return (
    <ModalShell
      open={open}
      ariaLabel="Overdue remediation tasks"
      eyebrow="OASYS · OVERDUE"
      title="Overdue Remediation Work"
      subtitle="Review remediation tasks that have breached their SLA, due date, or compliance deadline. These should be triaged before lower-risk queues."
      onClose={onClose}
      maxWidth="1120px"
      accentBorder="rgba(248, 113, 113, 0.46)"
    >
      <div style={metricGridStyle}>
        <MetricTile
          label="Overdue Tasks"
          value={effectiveTotalCount}
          sub={shownCount === effectiveTotalCount ? `${framework} breached deadlines` : `${shownCount} loaded for review`}
          color="#f87171"
        />
        <MetricTile
          label="High Priority"
          value={highPriorityCount}
          sub="priority escalation"
          color={highPriorityCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Unassigned"
          value={unassignedCount}
          sub="needs owner"
          color={unassignedCount > 0 ? "#fbbf24" : "#34d399"}
        />
        <MetricTile
          label="Oldest Breach"
          value={oldestBreachDays}
          sub={oldestBreachDays === 1 ? "day overdue" : "days overdue"}
          color={oldestBreachDays > 0 ? "#f87171" : "#34d399"}
        />
      </div>

      <ModalSection
        title="Overdue Task Activity"
        description="Use Review / Manage to update ownership or due dates, Fix Plan to complete the source remediation, or Guidance to view context."
        badge={loading ? "loading…" : `${shownCount} shown${effectiveTotalCount > shownCount ? ` of ${effectiveTotalCount}` : ""}`}
        badgeTone={sortedTasks.length > 0 ? "danger" : "ok"}
      >
        {error ? <div className="warn">{error}</div> : null}

        {loading && sortedTasks.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            Loading the full overdue remediation queue…
          </div>
        ) : sortedTasks.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            No overdue remediation tasks are currently loaded for this framework.
          </div>
        ) : (
          sortedTasks.map((task, index) => {
            const controlId = task.control_id || task.controlId || "";
            const ruleId = task.rule_id || task.ruleId || "";
            const resourceId = getTaskResource(task);
            const owner = cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "Unassigned");
            const statusLabel = getTaskStatusLabel(task.status);
            const validationLabel = getValidationLabel(task);
            const deadline = getDeadlineValue(task);

            return (
              <TaskQueueCard
                key={task.id || `${controlId}-${ruleId}-${index}`}
                title={getTaskTitle(task)}
                chips={[
                  { label: "Overdue", tone: "danger" },
                  { label: `P${safeNumber(task.priority, 100)}`, tone: isTaskHighPriority(task) ? "danger" : "default" },
                  { label: owner, tone: isTaskUnassigned(task) ? "warn" : "ok" },
                  validationLabel !== "Pending" ? { label: validationLabel, tone: normalizeText(validationLabel) === "failed" ? "danger" : "default" } : null,
                ]}
                dueLabel={compactDate(deadline)}
                meta={[
                  { label: "Control", value: cleanValue(controlId, "—") },
                  { label: "Rule", value: cleanValue(ruleId, "—") },
                  { label: "Resource", value: resourceId, wrap: true },
                ]}
                context={`${getOverdueLabel(task)} · Status ${statusLabel} · Source ${getSlaSource(task)}${task.failure_reason || task.validation_reason || task.description ? ` · ${task.failure_reason || task.validation_reason || task.description}` : ""}`}
                contextLabel="SLA breach"
                actions={[
                  { key: "review", label: "Review / Manage", onClick: () => onReviewTask(task, { returnTo: "overdue_tasks" }) },
                  { key: "fix", label: "Fix Plan", onClick: () => onFixPlan(task, "task", { returnTo: "overdue_tasks" }) },
                  { key: "guidance", label: "Guidance", onClick: () => onViewGuidance(task, { returnTo: "overdue_tasks" }) },
                  {
                    key: "drift",
                    label: "Open in Drift",
                    onClick: () =>
                      onOpenDrift({
                        framework: task.framework || framework,
                        controlId: controlId || undefined,
                        ruleId: ruleId || undefined,
                        resourceId: resourceId === "—" ? undefined : resourceId,
                      }),
                  },
                ]}
              />
            );
          })
        )}
      </ModalSection>
    </ModalShell>
  );
}
